// Phase 4 — creative-assets batch UI: collapsed batch card.
//
// Proposals sharing a non-null batch_id render as ONE card in the queue:
// a title line ("Launch · 3 proposals · Meta · Acme"), a creative thumbnail
// strip (first ~4 previews across items), status rollup chips, batch-level
// Approve all / Deny all (same RBAC gate as per-proposal actions), and an
// expand toggle that reveals the individual ProposalRow entries unchanged.
import { useTranslation } from 'react-i18next';
import type {
  CreativePreview,
  Proposal,
} from '../../../lib/api/proposals/proposals-api';
import {
  PlatformMark,
  Glyph,
  platformMeta,
  shopLabel,
  timeAgo,
} from './proposalFormat';
import {
  collectCreativePreviews,
  rollupBatchStatuses,
  batchPlatforms,
  batchShopLabels,
} from './batchGrouping';
import ProposalRow from './ProposalRow';

const THUMB_LIMIT = 4;

export function formatDurationMs(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// One tile of the thumbnail strip. Images with a live signed URL render as
// <img>; videos (and images whose signed URL is missing/expired) render as a
// placeholder tile — videos get a play glyph + duration badge.
function CreativeThumb({ preview }: { preview: CreativePreview }) {
  const { t } = useTranslation();
  const isImage = preview.kind === 'image';

  if (isImage && preview.preview_url) {
    return (
      <span className='aq-batch__thumb'>
        <img
          src={preview.preview_url}
          alt={t('Creative')}
          loading='lazy'
          // Signed URLs are short-lived — if one expires mid-session, degrade
          // to the placeholder styling instead of a broken-image glyph.
          onError={(e) => {
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      </span>
    );
  }

  return (
    <span
      className='aq-batch__thumb aq-batch__thumb--placeholder'
      title={isImage ? t('Image') : t('Video')}
    >
      <Glyph name={isImage ? 'image' : 'play'} size={16} />
      {preview.kind === 'video' && preview.duration_ms != null && (
        <span className='aq-batch__thumb-duration'>
          {formatDurationMs(preview.duration_ms)}
        </span>
      )}
    </span>
  );
}

interface BatchCardProps {
  batchId: string;
  proposals: Proposal[];
  expanded: boolean;
  highlighted?: boolean;
  onToggle: (batchId: string) => void;
  canApprove: boolean;
  canDeny: boolean;
  onApproveAll: (batchId: string, count: number) => void;
  onDenyAll: (batchId: string, count: number) => void;
  // Row passthrough — the expanded view reuses ProposalRow unchanged.
  activeProposalId?: string;
  requesterName: (p: Proposal) => string | undefined;
  onOpen: (p: Proposal) => void;
  onApprove: (p: Proposal) => void;
  onDeny: (p: Proposal) => void;
  isActionable: (status: string) => boolean;
}

const BatchCard = ({
  batchId,
  proposals,
  expanded,
  highlighted = false,
  onToggle,
  canApprove,
  canDeny,
  onApproveAll,
  onDenyAll,
  activeProposalId,
  requesterName,
  onOpen,
  onApprove,
  onDeny,
  isActionable,
}: BatchCardProps) => {
  const { t } = useTranslation();

  const rollup = rollupBatchStatuses(proposals);
  const platforms = batchPlatforms(proposals);
  const shops = batchShopLabels(proposals, shopLabel);
  const previews = collectCreativePreviews(proposals, THUMB_LIMIT);
  const totalPreviews = proposals.reduce(
    (sum, p) => sum + (p.creative_previews?.length ?? 0),
    0
  );
  const overflow = totalPreviews - previews.length;
  const actionableCount = proposals.filter((p) =>
    isActionable(p.status)
  ).length;
  const newest = proposals[0];

  const platformLabel = platforms
    .map((slug) => platformMeta(slug).short)
    .join(', ');
  const shopsLabel = shops.join(', ');

  const chips: Array<{ key: string; label: string; tone: string; count: number }> =
    [
      { key: 'pending', label: t('Pending'), tone: 'pending', count: rollup.pending },
      { key: 'approved', label: t('Approved'), tone: 'approved', count: rollup.approved },
      { key: 'denied', label: t('Denied'), tone: 'denied', count: rollup.denied },
      { key: 'failed', label: t('Failed'), tone: 'failed', count: rollup.failed },
    ].filter((c) => c.count > 0);

  return (
    <div
      id={`aq-batch-${batchId}`}
      className={`aq-batch ${expanded ? 'aq-batch--expanded' : ''} ${
        highlighted ? 'aq-batch--highlighted' : ''
      }`}
    >
      <div
        className='aq-batch__head'
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${t('Launch')} · ${rollup.total} ${t(
          'BatchProposalsNoun'
        )} · ${platformLabel}`}
        onClick={() => onToggle(batchId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(batchId);
          }
        }}
      >
        <div className='aq-batch__marks' aria-hidden='true'>
          {platforms.slice(0, 3).map((slug) => (
            <PlatformMark key={slug} platform={slug} size={30} />
          ))}
        </div>

        <div className='aq-batch__main'>
          <div className='aq-batch__title'>
            <span className='aq-batch__title-kind'>{t('Launch')}</span>
            <span className='aq-batch__title-sep'>·</span>
            <span>
              {rollup.total} {t('BatchProposalsNoun')}
            </span>
            <span className='aq-batch__title-sep'>·</span>
            <span>{platformLabel}</span>
            {shopsLabel && (
              <>
                <span className='aq-batch__title-sep'>·</span>
                <span className='aq-batch__title-shop'>{shopsLabel}</span>
              </>
            )}
          </div>

          <div className='aq-batch__chips'>
            {chips.map((chip) => (
              <span
                key={chip.key}
                className={`aq-batch__chip aq-batch__chip--${chip.tone}`}
              >
                {chip.count} {chip.label}
              </span>
            ))}
            {newest && (
              <span className='aq-batch__time'>
                {timeAgo(newest.created_at)}
              </span>
            )}
          </div>
        </div>

        {previews.length > 0 && (
          <div className='aq-batch__strip' aria-hidden='true'>
            {previews.map((preview) => (
              <CreativeThumb key={preview.asset_id} preview={preview} />
            ))}
            {overflow > 0 && (
              <span className='aq-batch__thumb aq-batch__thumb--more'>
                +{overflow}
              </span>
            )}
          </div>
        )}

        <div
          className='aq-batch__actions'
          onClick={(e) => e.stopPropagation()}
        >
          {actionableCount > 0 && canApprove && (
            <button
              type='button'
              className='aq-batch__btn aq-batch__btn--approve'
              onClick={() => onApproveAll(batchId, actionableCount)}
            >
              <Glyph name='check' size={14} stroke={2.1} /> {t('ApproveAll')}
            </button>
          )}
          {actionableCount > 0 && canDeny && (
            <button
              type='button'
              className='aq-batch__btn aq-batch__btn--deny'
              onClick={() => onDenyAll(batchId, actionableCount)}
            >
              <Glyph name='x' size={14} stroke={2.1} /> {t('DenyAll')}
            </button>
          )}
          <span
            className={`aq-batch__chevron ${
              expanded ? 'aq-batch__chevron--open' : ''
            }`}
            title={expanded ? t('HideProposals') : t('ShowProposals')}
          >
            <Glyph name='chevronDown' size={16} stroke={2} />
          </span>
        </div>
      </div>

      {expanded && (
        <div className='aq-batch__rows'>
          {proposals.map((proposal) => (
            <ProposalRow
              key={proposal.id}
              proposal={proposal}
              active={activeProposalId === proposal.id}
              showStatus
              requesterName={requesterName(proposal)}
              onOpen={onOpen}
              onApprove={onApprove}
              onDeny={onDeny}
              showActions={
                (canApprove || canDeny) && isActionable(proposal.status)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BatchCard;
